import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo } from 'react';
import invariant from 'invariant';
import { useDispatch, useSelector, useStore } from 'react-redux';
import isNil from 'lodash/isNil';
import { GridActions } from '../../store/grid/slice';
import { getActivePage, getColumnFilters, getFilter, getPageSize, getSelectedItemId, getSort } from './selectors';
import { unsafe_Grid as Grid } from './Grid';
function isImmutableList(input) {
    return !!input.isEmpty;
}
function useIdSelector(id, selector) {
    const sel = useCallback((state)=>selector(state, {
            id
        }), [
        id,
        selector
    ]);
    return useSelector(sel);
}
const useGridInvariant = (id)=>{
    const store = useStore();
    useEffect(()=>{
        if (process.env.NODE_ENV !== 'production') {
            invariant(id, 'Grid is missing required id prop.');
            invariant(store.getState().grid, `grid is missing in the redux store. Make sure you are using the gridReducer.

import { configureStore } from '@reduxjs/toolkit';
import { gridReducer } from '@cancercentrum/rcc-react';

const store = configureStore({
    reducer:
        grid: gridReducer,
    },
    [...]
});
`);
        }
    }, [
        store,
        id
    ]);
};
export function defaultGetFieldValue(item, col) {
    const val = item.data[col.field];
    return isNil(val) ? col.fallbackValue : val;
}
const defaultGetKey = (item)=>item.id;
const DefaultSort = [];
function ReduxGrid(props) {
    const { id, rows, columns, renderRow, getFieldValue = defaultGetFieldValue, defaultSort, getKey = defaultGetKey, ...rest } = props;
    const dispatch = useDispatch();
    const rowsAsArray = useMemo(()=>{
        return isImmutableList(rows) ? rows.toArray() : rows;
    }, [
        rows
    ]);
    useGridInvariant(id);
    const filter = useIdSelector(id, getFilter);
    const columnFilters = useIdSelector(id, getColumnFilters);
    const sort = useIdSelector(id, getSort) || defaultSort || DefaultSort;
    const pageSize = useIdSelector(id, getPageSize) || props.defaultPageSize;
    const activePage = useIdSelector(id, getActivePage);
    const selectedItemId = useIdSelector(id, getSelectedItemId);
    const cols = columns.map((x, i)=>{
        const renderCell = x.renderCell;
        return {
            type: 'string',
            id: (x.id || x.field || 'col__' + i).toString(),
            getValue: (a)=>{
                let val = '';
                if (x.getStringValue) {
                    val = x.getStringValue(a);
                } else if (x.field) {
                    val = getFieldValue(a, x);
                }
                return (isNil(val) ? '' : val).toString();
            },
            renderCell: renderCell ? (opts)=>{
                return renderCell({
                    row: opts.row,
                    filter: opts.filter,
                    column: x,
                    items: opts.items
                });
            } : undefined,
            sortable: x.sortable,
            header: x.header,
            searchable: x.searchable,
            filters: x.filters,
            tdProps: x.tdProps,
            thProps: x.thProps,
            filterable: !!x.filters && x.filters.length > 0
        };
    });
    return /*#__PURE__*/ _jsx(Grid, {
        ...rest,
        columns: cols,
        rows: rowsAsArray,
        getId: getKey,
        renderRow: renderRow ? (opts)=>{
            return renderRow({
                row: opts.row,
                renderCells: opts.renderCells,
                sort: opts.sort,
                items: opts.items,
                getFieldValue: (item, col)=>{
                    return getFieldValue(item, col);
                },
                columns: columns
            });
        } : undefined,
        filter: filter,
        onFilterChange: (x)=>dispatch(GridActions.setFilter(id, x)),
        value: selectedItemId,
        onValueChange: (x)=>dispatch(GridActions.setSelectedItemId(id, x)),
        columnFilters: columnFilters,
        onColumnFiltersChange: (x)=>dispatch(GridActions.setColumnFilters(id, x)),
        sort: sort,
        onSortChange: (x)=>dispatch(GridActions.setSort(id, x)),
        pageSize: pageSize,
        onPageSizeChange: (x)=>dispatch(GridActions.setPageSize(id, x)),
        activePage: activePage,
        onActivePageChange: (x)=>dispatch(GridActions.setActivePage(id, x))
    });
}
export default /*#__PURE__*/ memo(ReduxGrid);
